<template>
    <div v-if="!!model">
        <rkm-area
            title="Exportação"
            type="fieldset"
        >
            <rkm-form ref="FormExportacao">
                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :options="form.competencias"
                            :validations="validations.competencia"
                            label="Competência"
                            show-default-option
                            v-model="model.competencia"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="store"
                            color="primary"
                        >
                            Exportar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            title="Arquivos exportados"
            type="fieldset"
        >
            <rkm-table
                :fields="fields.arquivosExportados"
                :items="items.arquivosExportados"
            >
                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        :disabled="item.arquivo === null"
                        @click="download(item)"
                        color="primary"
                        small
                        title="Download"
                    >
                        <rkm-icon icon="download" />
                    </rkm-button>

                    <rkm-button
                        :disabled="item.statusId === enums.exportacaoStatus.AGUARDANDO_PROCESSAMENTO || item.statusId === enums.exportacaoStatus.EM_PROCESSAMENTO"
                        @click="destroy(item)"
                        authorize="destroy-atencao-basica-exportacao"
                        color="danger"
                        small
                        title="Excluir"
                    >
                        <rkm-icon icon="trash" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            title="Fichas exportadas"
            type="fieldset"
        >
            <rkm-form ref="FormFichaExportada">
                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :options="form.competenciasFichasExportadas"
                            :validations="validations.competenciaFichaExportada"
                            label="Competência"
                            show-default-option
                            v-model="model.competenciaFichaExportada"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="searchFicha"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>

                <rkm-table
                    :fields="fields.fichasExportadas"
                    :items="items.fichasExportadas"
                >
                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click="$refs.Modal.open(item)"
                            title="Registros"
                            color="primary"
                            small
                        >
                            <rkm-icon icon="search" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-form>
        </rkm-area>

        <Modal
            :form="form"
            :model="model"
            ref="Modal"
        />
    </div>
</template>

<script>
// Components.
import Modal from '@/components/atencao-basica/exportacao/Modal'

// Enums.
import exportacaoStatus from '@/enums/atencao-basica/exportacaoStatus'

// Mixins
import { error, repository, stringCase } from "@/mixins"

// Vuex.
import { mapActions } from "vuex"

export default {
    components: { Modal },
    computed: {
        validations() {
            return {
                competencia: {required: true},
                competenciaFichaExportada: {required: true},
            }
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('atencaoBasica.exportacao')
        }

        this.load()
    },
    data: () => ({
        enums: {exportacaoStatus},
        form: null,
        model: null,
        fields: {
            arquivosExportados: [
                {key: 'dataHora', label: 'Data/Hora'},
                {key: 'competencia', label: 'Competência'},
                {key: 'arquivo', label: 'Nome do arquivo'},
                {key: 'usuarioNome', label: 'Profissional'},
                {key: 'statusNome', label: 'Status'},
                {class: 'col-min text-center', key: 'actions', label: 'Ações'},
            ],
            fichasExportadas: [
                {key: 'data', label: 'Data da exportação'},
                {key: 'dataFicha', label: 'Data da ficha'},
                {key: 'usuario', label: 'Profissional da exportação'},
                {key: 'uuid', label: 'UUID'},
                {key: 'funcionalidade', label: 'Funcionalidade'},
                {key: 'quantidade', label: 'Quantidade de registro(s)'},
                {class: 'col-min', key: 'actions', label: 'Ações'},
            ],
        },
        items: {
            arquivosExportados: [],
            fichasExportadas: [],
        },
    }),
    methods: {
        ...mapActions([
            'setExportacaoEsusEmAndamento',
            'setLoading',
            'unsetLoading',
        ]),
        load() {
            this.setLoading()

            this.repositories.exportacao.create()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.items.arquivosExportados = response.data.arquivosExportados
                    this.$emit('versao-integrador-centralizador', this.form.versaoIntegradorEsus)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store() {
            this.$refs.FormExportacao.validate()
                .then(valid => {
                    if (!valid) return

                    this.setLoading()

                    const request = {competencia: this.model.competencia}

                    this.repositories.exportacao.store(this.camelObjectKeysToSnakeRecursive(request))
                        .then(() => {
                            this.showSaveNotification()
                            this.reset()
                            this.load()
                            this.setExportacaoEsusEmAndamento(true)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        searchFicha() {
            this.$refs.FormFichaExportada.validate()
                .then(valid => {
                    if (!valid) return

                    this.setLoading()

                    const request = {
                        competenciaFichaExportada: this.model.competenciaFichaExportada,
                        listar: 'fichas',
                    }

                    this.repositories.exportacao.index(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => this.items.fichasExportadas = response.data)
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        download(item) {
            this.setLoading()

            this.repositories.exportacao.download(item.id)
                .then(response => {
                    const file = response.headers['content-disposition'].split('filename=')[1]
                    const blob = new Blob([response.data])
                    const link = document.createElement('a')

                    link.href = URL.createObjectURL(blob)
                    link.download = file
                    link.click()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        destroy(item) {
            this.showDeleteConfirmation(
                'Deseja realmente excluir o arquivo?',
                async () => {
                    this.setLoading()

                    this.repositories.exportacao.destroy(item.id)
                        .then(() => {
                            this.items.arquivosExportados.splice(this.items.arquivosExportados.findIndex(i => i.id === item.id), 1)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            )
        },
        reset() {
            this.model.competencia = null
            this.model.competenciaFichaExportada = null

            this.$refs.FormExportacao.resetValidator()
            this.$refs.FormFichaExportada.resetValidator()
        },
    },
    mixins: [error, repository, stringCase],
}
</script>